import React from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { CaseStatus } from '../../types';
import { t } from '../../utils/translations';

interface CasesToolbarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  statusFilter: CaseStatus | 'all';
  onStatusFilterChange: (status: CaseStatus | 'all') => void;
  filteredCount: number;
  activeCasesCount: number;
  waitingCasesCount: number;
}

interface FilterPillProps {
  label: string;
  active: boolean;
  count?: number;
  onClick: () => void;
}

const FilterPill: React.FC<FilterPillProps> = ({ label, active, count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition duration-200 active:scale-[0.98] ${
      active
        ? 'bg-brand-600 text-white'
        : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
    }`}
  >
    {label}
    {count !== undefined && count > 0 && (
      <span
        className={`rounded-full px-1.5 text-xs font-semibold ${
          active ? 'bg-white/25 text-white' : 'bg-slate-100 text-slate-600'
        }`}
      >
        {count}
      </span>
    )}
  </button>
);

const CasesToolbar: React.FC<CasesToolbarProps> = ({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  filteredCount,
  activeCasesCount,
  waitingCasesCount,
}) => {
  const countLabel = filteredCount === 1 ? 'caz' : 'cazuri';

  return (
    <div className="mb-4 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative lg:w-72">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input
            id="cases-search"
            type="search"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t.cases.filters.searchPlaceholder || 'Căutați cazuri'}
            aria-label={t.cases.searchLabel}
            className="w-full rounded-lg border border-slate-200 py-2 pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <FilterPill
            label={t.status.active}
            active={statusFilter === 'active'}
            count={activeCasesCount}
            onClick={() => onStatusFilterChange('active')}
          />
          <FilterPill
            label={t.status.waiting}
            active={statusFilter === 'waiting'}
            count={waitingCasesCount}
            onClick={() => onStatusFilterChange('waiting')}
          />
          <FilterPill
            label={t.status.completed}
            active={statusFilter === 'finished'}
            onClick={() => onStatusFilterChange('finished')}
          />
          <FilterPill
            label={t.status.all}
            active={statusFilter === 'all'}
            onClick={() => onStatusFilterChange('all')}
          />
        </div>

        <span className="text-sm text-slate-500 lg:ml-auto">
          {filteredCount} {countLabel}
        </span>
      </div>
    </div>
  );
};

export default CasesToolbar;
